import React from 'react';
import { Link } from 'react-router-dom';

/*
 * /trust-centre — the "Verify" slot for section 2 of the homepage.
 *
 * Renamed from "clinical governance" deliberately. The buyer is HR, not a
 * clinician, and "clinical" reads as medical claim territory we are not in
 * before Class IIa. Trust centre is the term the market already uses.
 *
 * The load-bearing content is the two-column boundary table. The objection
 * this page answers is never "is it secure", it is "will my staff think we
 * are watching them" — so the boundary is stated as what an employer cannot
 * see, first, in the employee's own words.
 *
 * The promise section arrived from the homepage on 2 Sep. Same reasoning: it
 * is what we will and will not stand behind, which is a trust question.
 */

const TrustCentrePage = () => (
    <div className="pa2">
        <section className="hero" id="top">
            <div className="wrap">
                <p className="kick">Trust centre</p>
                <h1 className="on-dark">
                    <span className="setup">What your employer sees.</span>
                    <span className="payoff">And what they never will.</span>
                </h1>
                <p className="lede">
                    An employer covers the service. That does not give them your health record,
                    and no setting anywhere lets them buy it. This page states the boundary in
                    full so it can be checked before anyone signs anything.
                </p>
            </div>
        </section>

        <section className="tint">
            <div className="wrap">
                <h2><span className="setup">The boundary.</span></h2>
                <div className="contrast">
                    <div className="cc no">
                        <h3>An employer never sees</h3>
                        <ul>
                            <li>Who has enrolled, by name</li>
                            <li>Any individual&rsquo;s symptoms, pain scores or history</li>
                            <li>Any individual&rsquo;s programme, adherence or activity</li>
                            <li>Anything said to a physiotherapist</li>
                            <li>Any group small enough to identify a person</li>
                        </ul>
                    </div>
                    <div className="cc yes">
                        <h3>An employer sees</h3>
                        <ul>
                            <li>How many people they cover, against total workforce</li>
                            <li>What proportion are participating</li>
                            <li>Aggregate adherence over time</li>
                            <li>Time from provisioning to live</li>
                            <li>Nothing below a cohort of fifty</li>
                        </ul>
                    </div>
                </div>
                <p className="cap" style={{ marginTop: '1rem' }}>
                    The cohort minimum is enforced in CMPX. A view that would fall below fifty
                    people does not render a suppressed number, it does not render at all.
                </p>
            </div>
        </section>

        <section className="white">
            <div className="wrap">
                <h2><span className="setup">How the data is handled.</span></h2>
                <table className="ref">
                    <tbody>
                        <tr><th>Question</th><th>Position</th></tr>
                        <tr><td><b>Who is the data controller?</b></td><td>PhysioActivity is the controller for the health data an individual generates. The employer is never a controller or processor of it, which is what keeps the boundary above enforceable rather than a policy promise.</td></tr>
                        <tr><td><b>Where is data stored?</b></td><td>United Kingdom and European Union regions only.</td></tr>
                        <tr><td><b>Is data sold or shared?</b></td><td>No. Not to insurers, not to employers, not to advertisers.</td></tr>
                        <tr><td><b>Is data used for research?</b></td><td>Only in de-identified form, only with explicit opt-in, and opt-in is reversible at any point without affecting the programme.</td></tr>
                        <tr><td><b>What happens if someone leaves?</b></td><td>The account is theirs. It does not close when their employment ends, and the employer is not told they kept it.</td></tr>
                        <tr><td><b>Can someone export or delete everything?</b></td><td>Yes, both, from the app, and deletion is permanent.</td></tr>
                        <tr><td><b>Who can see records internally?</b></td><td>The registered physiotherapist responsible for that person&rsquo;s care, and named engineers under access control for support. Every access is logged.</td></tr>
                        <tr><td><b>Is this a medical device?</b></td><td>No, and we do not present it as one. See <Link to="/blog/who-writes-your-programme">who writes your programme</Link> for what we do and do not claim.</td></tr>
                    </tbody>
                </table>

            </div>
        </section>

        {/* Moved here from the homepage on 2 Sep. It is contract language — it
            answers what an employer is buying and what we refuse to assert —
            which belongs where someone is already checking what we can and
            cannot do, not on a landing page read by everyone. */}
        <section className="s-promise" id="promise">
            <div className="wrap">
                <h2 className="on-dark">
                    <span className="setup">What we commit to.</span>
                    <span className="payoff">And what we do not claim.</span>
                </h2>
                <p className="lede">
                    The unit is the covered life, so the cost is simple to budget and capped in
                    advance. Being clear about what we measure, and what we do not claim, is part
                    of the same thing.
                </p>

                <div className="prom">
                    <div className="pc">
                        <div className="k">Committed</div>
                        <h3>Coverage</h3>
                        <p>Everyone you provision, not only the people who enrol. Reported against your whole workforce so the gap stays visible.</p>
                    </div>
                    <div className="pc">
                        <div className="k">Committed</div>
                        <h3>Time to support</h3>
                        <p>A service level you can benchmark and enforce, on the escalation tier.</p>
                    </div>
                    <div className="pc rep">
                        <div className="k">Reported, not committed</div>
                        <h3>Adherence</h3>
                        <p>We cannot commit to people doing their exercises. We can show you how much is actually happening.</p>
                    </div>
                    <div className="pc ref">
                        <div className="k">Not claimed</div>
                        <h3>Clinical outcomes and savings</h3>
                        <p>We do not claim these, because we cannot yet evidence them. What you are buying is reach and access, and your own figures close the chain.</p>
                    </div>
                </div>
            </div>
        </section>

        {/* The hub. Privacy and Terms stay as their own documents at their own
            URLs: a DPO, a procurement reviewer or a regulator expects to find
            and cite them as documents, and they must not move underneath a page
            that changes as the product does. What this page adds is the plain
            English version and a single front door to everything else.

            The "on request" items are listed as on request deliberately. We do
            not have a sub-processor page or a DPIA summary published yet, and
            listing them as if we did would be the exact thing this page exists
            to rule out. */}
        <section className="tint" id="documents">
            <div className="wrap">
                <h2>
                    <span className="setup">Everything in one place.</span>
                </h2>
                <p className="lede">
                    This page explains the position in plain English. These are the documents
                    behind it.
                </p>

                <div className="cards">
                    <div className="card">
                        <h3>Privacy notice</h3>
                        <p>The legal document. What we collect, on what basis, how long we keep it, and your rights over it.</p>
                        <p className="quiet"><Link to="/privacy">Read the privacy notice</Link></p>
                    </div>
                    <div className="card">
                        <h3>Terms of service</h3>
                        <p>The terms you or your organisation agree to when using the platform.</p>
                        <p className="quiet"><Link to="/TermsOfService">Read the terms</Link></p>
                    </div>
                    <div className="card">
                        <h3>Who writes your programme</h3>
                        <p>Clinical accountability: who is responsible, how they are verified, and the claims we do not make.</p>
                        <p className="quiet"><Link to="/blog/who-writes-your-programme">Read it</Link></p>
                    </div>
                </div>

                <p className="quiet" style={{ marginTop: '1.4rem' }}>
                    <b>Available on request for procurement and security review:</b> our security
                    documentation, sub-processor list, data protection impact assessment, and
                    incident response process. Ask and we will send them.
                </p>
            </div>
        </section>

        {/* This page had no call to action at all. Its reader is an employer
            checking the boundary before committing, which is the point they are
            closest to acting, and the only route onward was a link inside a
            paragraph. Every other page closes on a button. */}
        <section className="s-promise" id="ask">
            <div className="wrap">
                <h2 className="on-dark">
                    <span className="setup">Press us on any of this.</span>
                </h2>
                <p className="lede">
                    Security documentation for procurement is available on request, and if
                    something here is not specific enough for your own review, tell us which part.
                </p>
                <div className="cta-row">
                    <Link className="btn btn-t" to="/contact">Contact us</Link>
                </div>
            </div>
        </section>

    </div>
);

export default TrustCentrePage;
